import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { getModuleMapKey } from '../../helpers/moduleMapKey'

type PermissionsContextModel = {
  apiVersion: string
  permissionsPerModuleMap: Map<string, Map<string, number[]>>
  systemPermissions: Map<string, string[]>
  addPermissionsForModule: (
    clinicId: number,
    module: string,
    permissions: Map<string, number[]>,
  ) => void
  getPermissionsForModule: (
    clinicId: number,
    module: string,
  ) => Map<string, number[]> | undefined
  addSystemPermissions: (
    clinicId: number,
    systemPermissionsPerModule: Record<string, string[]>,
  ) => void
  getSystemPermissionsForModule: (
    clinicId: number,
    module: string,
  ) => string[] | undefined
  resetPermissionsMapsIfNeeded: (clinicId: number) => void
}

const PermissionsContext = createContext<PermissionsContextModel>({
  apiVersion: '',
  permissionsPerModuleMap: new Map<string, Map<string, number[]>>(),
  systemPermissions: new Map<string, string[]>(),
  addPermissionsForModule: () => {},
  getPermissionsForModule: () => undefined,
  addSystemPermissions: () => {},
  getSystemPermissionsForModule: () => undefined,
  resetPermissionsMapsIfNeeded: () => {},
})

export function PermissionsContextProvider({
  children,
  apiVersion,
}: PropsWithChildren<{ apiVersion: string }>) {
  const [systemPermissions, setSystemPermissions] = useState(
    new Map<string, string[]>(),
  )
  const [permissionsPerModuleMap, setPermissionsPerModuleMap] = useState(
    new Map<string, Map<string, number[]>>(),
  )

  const resetPermissionsMapsIfNeeded = useCallback(
    (clinicId: number) => {
      if (
        !Array.from(systemPermissions.keys()).every((key) =>
          key.includes(`${clinicId}`),
        )
      ) {
        setSystemPermissions(new Map<string, string[]>())
      }

      if (
        !Array.from(permissionsPerModuleMap.keys()).every((key) =>
          key.includes(`${clinicId}`),
        )
      ) {
        setPermissionsPerModuleMap(new Map<string, Map<string, number[]>>())
      }
    },
    [systemPermissions, permissionsPerModuleMap],
  )

  const addPermissionsForModule = useCallback(
    (clinicId: number, module: string, permissions: Map<string, number[]>) => {
      setPermissionsPerModuleMap(
        new Map(
          permissionsPerModuleMap.set(
            getModuleMapKey(clinicId, module),
            permissions,
          ),
        ),
      )
    },
    [permissionsPerModuleMap],
  )

  const getPermissionsForModule = useCallback(
    (clinicId: number, module: string) =>
      permissionsPerModuleMap.get(getModuleMapKey(clinicId, module)),
    [permissionsPerModuleMap],
  )

  const addSystemPermissions = useCallback(
    (
      clinicId: number,
      systemPermissionsPerModule: Record<string, string[]>,
    ) => {
      const allModules = Object.keys(systemPermissionsPerModule)
      const newSystemPermissions = new Map<string, string[]>()

      allModules.forEach((module) =>
        newSystemPermissions.set(
          getModuleMapKey(clinicId, module),
          systemPermissionsPerModule[module],
        ),
      )

      setSystemPermissions(newSystemPermissions)
    },
    /**
     * ⚠️ CAVEAT:
     * ESLINT here is not triggering missing dependencies used in useCallback,
     * maybe we should use useHandler instead?!
     */
    [],
  )

  const getSystemPermissionsForModule = useCallback(
    (clinicId: number, module: string) =>
      systemPermissions.get(getModuleMapKey(clinicId, module)),
    [systemPermissions],
  )

  const contextValue = useMemo(
    () => ({
      apiVersion,
      permissionsPerModuleMap,
      systemPermissions,
      addPermissionsForModule,
      getPermissionsForModule,
      addSystemPermissions,
      getSystemPermissionsForModule,
      resetPermissionsMapsIfNeeded,
    }),
    [
      apiVersion,
      permissionsPerModuleMap,
      systemPermissions,
      addPermissionsForModule,
      getPermissionsForModule,
      addSystemPermissions,
      getSystemPermissionsForModule,
      resetPermissionsMapsIfNeeded,
    ],
  )

  return (
    <PermissionsContext.Provider {...{ value: contextValue }}>
      {children}
    </PermissionsContext.Provider>
  )
}

export const usePermissionContext = () => useContext(PermissionsContext)
